import { DatePipe } from "@angular/common";
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, NgZone, ViewChild } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { RowSelectedEvent } from "ag-grid-community";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { Profile } from "../../../models/profile";
import { UserAssist } from "../../../models/userAssist";
import { CloudStorageService } from "../../../services/cloud-storage";
import { ConfirmService } from "../../../services/confirmation.service";
import { SiteParameterService } from "../../../services/siteparameter.service";

declare var ResizeObserver: any;

@Component({
  selector: 'app-assistant-access',
  templateUrl: './assistant-access.component.html',
  styles: [
  ]
})
export class AssistantAccessComponent implements OnInit, OnDestroy {

  @ViewChild('gridWrapper')
  set gridWrapper(ref: ElementRef<HTMLElement> | undefined) {
    if (!ref) {
      // Grid wrapper is being removed from the DOM —
      // clear the api reference so any in-flight observer callbacks bail out
      this.disconnectObserver();
      this.gridApi = undefined;
      this.gridColumnApi = undefined;
      return;
    }
    if (ref.nativeElement === this.observedElement) {
      return;
    }
    this.disconnectObserver();
    this.observedElement = ref.nativeElement;
    this.resizeObserver = new ResizeObserver((entries: { contentRect: { width: number; }; }[]) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0 && this.gridApi) {
        this.ngZone.run(() => this.resizeGrid());
      }
    });
    this.resizeObserver.observe(this.observedElement);
  }

  private impersonationSubscription: Subscription;

  public showList: boolean = true;
  public showDetail: boolean = false;
  public showAddButton: boolean = true;
  public showUpdateForm: boolean = false;
  public showAddForm: boolean = false;
  //public showAssistUserIDFound: boolean = false;
  //public showAssistUserIDNotFound: boolean = false;

  public selectedUserAssistId: string = "";
  //public selectedAssistUserId: string = "";
  public selectedAssistUserEmail: string = "";
  public selectedAssistUserStartDate: string = "";
  public selectedAssistUserEndDate: string = "";

  public isNoEndDate: boolean = false;
  public noEndDateValue = new Date(2099, 11, 31); // December 31, 2099

  public addErrorMessage: string = "";
  public updateErrorMessage: string = "";
  public startDateError: string = "";
  public endDateError: string = "";
  public emailError: string = "";
  private readonly emailPattern = /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  public saving = false;
  public profile!: Profile;
  public invitations$?: Observable<UserAssist[]>;
  public selectedinvitation$?: Observable<UserAssist>;
  private priorAssistUserEndDate: string = "";
  private gridApi: any;
  public noRowsTemplate: string = "";
  private gridColumnApi: any;
  private resizeObserver?: typeof ResizeObserver;
  private observedElement?: HTMLElement;

  private refresh$ = new BehaviorSubject<void>(undefined);

  public get assistUserEndDateDisplay(): string {
    return this.isNoEndDate ? '' : this.selectedAssistUserEndDate;
  }
  public set assistUserEndDateDisplay(value: string) {
    // While "No End Date" is checked the control is disabled; ignore stray writes
    // so the 12/31/2099 sentinel can never be clobbered by the view.
    if (this.isNoEndDate) {
      return;
    }
    this.selectedAssistUserEndDate = value ?? '';
  }

  private dateFormatter = (params: any): string => {
    if (!params.value) return '';
    const d = new Date(params.value);
    if (isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  private endDateFormatter = (params: any): string => {
    if (!params.value) return '';
    const d = new Date(params.value);
    if (isNaN(d.getTime())) return '';
    if (d.getFullYear() === this.noEndDateValue.getFullYear() &&
      d.getMonth() === this.noEndDateValue.getMonth() &&
      d.getDate() === this.noEndDateValue.getDate()) {
      return 'No End Date';
    }
    return this.dateFormatter(params);
  };

  columnDefs = [
    { field: 'assistUserEmail', headerName: 'Assistant Email', minWidth: 300 },
    { field: 'startDate', headerName: 'Start Date', minWidth: 125, valueFormatter: this.dateFormatter },
    { field: 'endDate', headerName: 'End Date', minWidth: 125, valueFormatter: this.endDateFormatter },
    //{
    //  headerName: `Linked`, headerClass: 'center-align-header', minWidth: 100, cellRenderer: (params: any) => { return this.getLinkedIcon(params.data); },
    //},
  ];

  defaultColumnDef = {
    sortable: true,
    resizable: true,
  };

  constructor(private siteParmService: SiteParameterService, private cloudStorageService: CloudStorageService,
    private datePipe: DatePipe, private confirmService: ConfirmService, private ngZone: NgZone) {
    this.noRowsTemplate = this.siteParmService.getGridNoResults();
    this.impersonationSubscription = this.siteParmService.getImpersonationUpdate().subscribe
      (message => {
        this.profile = siteParmService.getStaticProfile();
        this.invitationList();
      });
  }

  invitationList(): void {
    this.profile = this.siteParmService.getStaticProfile();
    //this.invitations$ = this.cloudStorageService.getUserAssists(this.profile.userId);
    this.invitations$ = this.refresh$.pipe(
      switchMap(() => this.cloudStorageService.getUserAssists(this.profile.userId))
    );
  }

  onRowClicked(event: RowSelectedEvent) {
    this.selectedUserAssistId = event.data.userAssistID;
    this.selectedAssistUserEmail = event.data.assistUserEmail;
    this.selectedAssistUserStartDate = this.datePipe.transform(event.data.startDate, 'MM/dd/yyyy') ?? '';
    this.selectedAssistUserEndDate = this.datePipe.transform(event.data.endDate, 'MM/dd/yyyy') ?? '';

    // A stored 12/31/2099 means the record was saved as "No End Date" —
    // tick the checkbox so the textbox comes up blank and disabled.
    this.isNoEndDate = this.isNoEndDateValue(event.data.endDate);
    this.priorAssistUserEndDate = '';
    this.updateErrorMessage = "";
    this.startDateError = "";
    this.endDateError = "";
    this.emailError = "";
    this.showList = false;
    this.showDetail = true;
    this.showAddButton = false;
    this.showUpdateForm = true;
    this.showAddForm = false;
  }

  onAddClicked() {
    this.selectedAssistUserEmail = '';
    this.selectedAssistUserStartDate = this.datePipe.transform(new Date(), 'MM/dd/yyyy') ?? '';
    this.selectedAssistUserEndDate = '';
    this.isNoEndDate = false;
    this.priorAssistUserEndDate = '';
    this.addErrorMessage = "";
    this.startDateError = "";
    this.endDateError = "";
    this.emailError = "";
    this.showList = false;
    this.showDetail = true;
    this.showAddForm = true;
    this.showUpdateForm = false;
    this.showAddButton = false;
  }

  closeDetail() {
    this.selectedUserAssistId = "";
    this.selectedAssistUserEmail = "";
    this.selectedAssistUserStartDate = "";
    this.selectedAssistUserEndDate = "";

    this.isNoEndDate = false;
    this.priorAssistUserEndDate = "";

    this.addErrorMessage = "";

    this.updateErrorMessage = "";
    this.startDateError = "";
    this.endDateError = "";
    this.emailError = "";
    this.showDetail = false;
    this.showList = true;
    this.showAddButton = true;

    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  inputFocused(event: any) {
    event.target.select()
  }

  public maskDate(value: string): string {
    if (!value) { return ''; }
    // Keep digits and separators only, and collapse doubled slashes
    let masked = value.replace(/[^\d\/]/g, '').replace(/\/{2,}/g, '/');
    // Insert the separator once a two-digit month, then day, has been typed.
    // Anchored and single-digit so hand-typed "1/5/2027" is left alone
    // while a pasted "12312099" is expanded in one pass.
    masked = masked
      .replace(/^(\d{2})(\d)/, '$1/$2')
      .replace(/^(\d{2}\/\d{2})(\d)/, '$1/$2');
    // Never more than the three mm/dd/yyyy segments
    const parts = masked.split('/');
    if (parts.length > 3) {
      masked = parts.slice(0, 3).join('/');
    }
    return masked.slice(0, 10);
  }

 /**
 * Blur normalizer for the Start/End Date textboxes.
 * Pads a single-digit month or day, fills in the current year when none was
 * typed, expands a two-digit year to 20yy, and rejects anything that is not a
 * real calendar date (02/30, month 13, and so on).
 *
 * Returns '' for an empty box, the normalized MM/dd/yyyy text on success, or
 * null when the value cannot be read as a date — the caller leaves the raw
 * text in place in that case so the user can see and correct what they typed.
 */
  public normalizeDate(value: string): string | null {
    const raw = (value ?? '').trim();
    if (!raw) { return ''; }
    const parts = raw.split('/').filter(p => p !== '');
    if (parts.length < 2 || parts.length > 3) { return null; }
    if (!parts.every(p => /^\d+$/.test(p))) { return null; }

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    let year: number;
    if (parts.length === 2) {
      // No year typed ("12/31" or "12/31/") — assume the current one
      year = new Date().getFullYear();
    } else if (parts[2].length === 2) {
      year = 2000 + Number(parts[2]);   // "27" -> 2027, "99" -> 2099
    } else if (parts[2].length === 4) {
      year = Number(parts[2]);
    } else {
      // A one-, three- or five-digit year is a typo, not a shorthand
      return null;
    }
    if (month < 1 || month > 12 || day < 1) { return null; }
    // Round-trip through Date to reject overflowing days like 02/30
    const parsed = new Date(year, month - 1, day);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
      return null;
    }
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const yyyy = String(year).padStart(4, '0');
    return `${mm}/${dd}/${yyyy}`;
  }
  public onStartDateBlur(): void {
    const normalized = this.normalizeDate(this.selectedAssistUserStartDate);
    if (normalized === null) {
      this.startDateError = 'Enter the start date as mm/dd/yyyy.';
      return;
    }
    this.startDateError = '';
    this.selectedAssistUserStartDate = normalized;
  }
  public onEndDateBlur(): void {
    // The control is disabled while "No End Date" is checked; nothing to normalize
    if (this.isNoEndDate) {
      this.endDateError = '';
      return;
    }
    const normalized = this.normalizeDate(this.selectedAssistUserEndDate);
    if (normalized === null) {
      this.endDateError = 'Enter the end date as mm/dd/yyyy or check No End Date.';
      return;
    }
    this.endDateError = '';
    this.selectedAssistUserEndDate = normalized;
  }
  /**
   * Blur handler for the add form's assistant email.
   * Trims first — a trailing space pasted out of Outlook or a spreadsheet is the
   * single most common reason a perfectly good address fails validation.
   */
  public onEmailBlur(): void {
    this.selectedAssistUserEmail = (this.selectedAssistUserEmail ?? '').trim();
    this.emailError = this.getEmailError(this.selectedAssistUserEmail);
  }
  /**
 * Single source of truth for the email message, shared by blur and submit.
 * The address is required, so an empty box is an error in both places —
 * unlike the dates, where blur stays lenient until submit.
 */
  private getEmailError(value: string): string {
    if (!value) {
      return "Enter the assistant's email address.";
    }
    if (!this.isValidEmail(value)) {
      return 'Enter a valid email address, for example name@company.com.';
    }
    return '';
  }
  /**
   * Angular's pattern plus one extra rule: the domain must contain a dot.
   * Angular allows "chris@lbig" on purpose so intranet hosts like user@localhost
   * validate, but an assistant invitation always goes to a public mailbox, so a
   * missing .com here is a typo rather than a valid address.
   * Drop the second condition if internal hostnames ever need to be accepted.
   */
  private isValidEmail(value: string): boolean {
    if (!this.emailPattern.test(value)) { return false; }
    const domain = value.slice(value.lastIndexOf('@') + 1);
    return domain.includes('.');
  }
  /**
   * Submit-time guard for the add form's email. Unlike blur, an empty box is an
   * error here — the invitation has to be addressed to somebody.
   * Not used by the update form, where the email is read-only.
   */
  private validateEmailForSave(): boolean {
    this.selectedAssistUserEmail = (this.selectedAssistUserEmail ?? '').trim();
    if (!this.selectedAssistUserEmail) {
      this.emailError = "Enter the assistant's email address.";
      return false;
    }
    if (!this.isValidEmail(this.selectedAssistUserEmail)) {
      this.emailError = 'Enter a valid email address, for example name@company.com.';
      return false;
    }
    this.emailError = '';
    return true;
  }
  /**
   * Submit-time guard. Blur stays lenient about an empty box so the form does
   * not shout at a half-filled row, but a date is required by the time we call
   * the service — this is what keeps an Invalid Date out of CloudStorageService.
   */
  private validateDatesForSave(): boolean {
    const start = this.normalizeDate(this.selectedAssistUserStartDate);
    if (!start) {
      this.startDateError = 'Enter the start date as mm/dd/yyyy.';
    } else {
      this.startDateError = '';
      this.selectedAssistUserStartDate = start;
    }
    if (this.isNoEndDate) {
      this.endDateError = '';
    } else {
      const end = this.normalizeDate(this.selectedAssistUserEndDate);
      if (!end) {
        this.endDateError = 'Enter the end date as mm/dd/yyyy or check No End Date.';
      } else {
        this.endDateError = '';
        this.selectedAssistUserEndDate = end;
      }
    }
    return !this.startDateError && !this.endDateError;
  }

  checkDate(event: KeyboardEvent) {
    // Ignore control keys like Backspace, Tab, Arrows
    if (event.key.length > 1) return;

    const input = event.target as HTMLInputElement;
    const projectedValue = input.value + event.key;

    //if (projectedValue.length > 7) {
    //  this.disableAdd = isNaN(Date.parse(projectedValue));
    //}

    //const timestamp = Date.parse(projectedValue);
    //if (isNaN(timestamp)) return false;
  }

  addInvitation(formData: any): void {
    this.addErrorMessage = '';
    // Both run unconditionally — && would short-circuit and hide the second
    // message, so the user would fix the email only to be told about the date.
    const emailOk = this.validateEmailForSave();
    const datesOk = this.validateDatesForSave();
    if (!emailOk || !datesOk) { return; }
    this.saving = true;
    this.cloudStorageService.addUserAssist(
      this.profile.userId, this.selectedAssistUserEmail,
      new Date(this.selectedAssistUserStartDate), this.getEndDateForSave())
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.saving = false;
          this.refresh$.next();
          this.closeDetail();
        },
        error: (err) => {
          this.saving = false;
          console.error('Failed to add invitation', err);
          this.addErrorMessage = 'Could not add invitation. Please try again.';
        }
      });
  }

  updateInvitation(formData: any): void {
    this.updateErrorMessage = '';
    if (!this.validateDatesForSave()) { return; }
    this.saving = true;
    this.cloudStorageService.updateUserAssist(
      /*this.selectedUserAssistId, this.profile.userId, this.selectedAssistUserId,*/
      this.selectedUserAssistId,
      new Date(this.selectedAssistUserStartDate), this.getEndDateForSave())
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.saving = false;
          this.refresh$.next();
          this.closeDetail();
        },
        error: (err) => {
          this.saving = false;
          console.error('Failed to update invitation', err);
          this.updateErrorMessage = 'Could not save changes. Please try again.';
        }
      });
  }

  async deleteInvitation(): Promise<void> {
    const message = 'Are you sure you want to delete this Agent Invitation?';
    const confirmed = await this.confirmService.confirm('Delete Invitation', message);
    if (confirmed) {
      this.cloudStorageService.deleteUserAssist(this.selectedUserAssistId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.refresh$.next();
            this.closeDetail();
          },
          error: (err) => {
            console.error('Failed to delete invitation', err);
            this.updateErrorMessage = 'Could not save changes. Please try again.';
          }
        });
    }
  }

  //searchUserByEmailAddress(formAgentIdCtrl: AbstractControl) {
  //  var emailAddress = formAgentIdCtrl.value;

  //  this.cloudStorageService.getUserByEmailAddress(emailAddress)
  //    .pipe(take(1))
  //    .subscribe(user => {
  //      if (!user) {
  //        this.showAssistUserIDFound = false;
  //        this.showAssistUserIDNotFound = true;
  //      }
  //      else {
  //        this.showAssistUserIDFound = true;
  //        this.showAssistUserIDNotFound = false;     
  //        formAgentIdCtrl.setValue(user.email);
  //        this.selectedAssistUserEmail = user.email;
  //        this.selectedAssistUserId = user.userId;          
  //      }
  //    });
  //}

  resetAddForm(): void {
    this.selectedUserAssistId = "";
    this.selectedAssistUserEmail = "";
    this.selectedAssistUserStartDate = this.datePipe.transform(new Date(), 'MM/dd/yyyy') ?? '';
    this.selectedAssistUserEndDate = "";
    this.isNoEndDate = false;
    this.priorAssistUserEndDate = "";
    this.addErrorMessage = "";
    this.updateErrorMessage = "";
    this.startDateError = "";
    this.endDateError = "";
    this.emailError = "";
  }

  /**
   * Fired by the "No End Date" checkbox on both the add and update forms.
   * [(ngModel)] has already flipped isNoEndDate by the time this runs, so the
   * event is only used to read the authoritative checked state.
   */
  checkNoEndDate(e: any): void {
    this.setNoEndDate(!!e?.target?.checked);
  }
  private setNoEndDate(checked: boolean): void {
    this.isNoEndDate = checked;
    // Checking the box satisfies the End Date requirement; unchecking gives the
    // user a clean box to type into, so either way the message is stale.
    this.endDateError = '';
    if (checked) {
      // Stash the hand-typed date (never the sentinel) so unchecking can restore it
      if (!this.isNoEndDateValue(this.selectedAssistUserEndDate)) {
        this.priorAssistUserEndDate = this.selectedAssistUserEndDate;
      }
      // Textbox renders blank via assistUserEndDateDisplay, but the field —
      // and therefore the service call — carries 12/31/2099.
      this.selectedAssistUserEndDate = this.datePipe.transform(this.noEndDateValue, 'MM/dd/yyyy') ?? '';
    } else {
      this.selectedAssistUserEndDate = this.priorAssistUserEndDate;
      this.priorAssistUserEndDate = '';
    }
  }
  /**
   * Single source of truth for what gets sent to CloudStorageService.
   * Guards against the field being out of sync with the checkbox.
   */
  private getEndDateForSave(): Date {
    return this.isNoEndDate
      ? new Date(this.noEndDateValue)
      : new Date(this.selectedAssistUserEndDate);
  }
  /** True when the supplied Date, date string, or MM/dd/yyyy text is 12/31/2099. */
  private isNoEndDateValue(value: any): boolean {
    if (!value) return false;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return false;
    return d.getFullYear() === this.noEndDateValue.getFullYear()
      && d.getMonth() === this.noEndDateValue.getMonth()
      && d.getDate() === this.noEndDateValue.getDate();
  }

  //getLinkedIcon(data: any): string {
  //  var iconString = '';
  //  iconString = data.assistUserID ? iconString +=
  //    '<i class="fas fa-lg fa-duotone fa-solid fa-link" style="--fa-primary-color: seagreen; --fa-secondary-color: firebrick; --fa-secondary-opacity: 0.84;"></i>' :
  //    '<i class="fas fa-lg fa-link-slash" style="--fa-secondary-opacity: 0.84;"></i>';
  //  return iconString;
  //}

  ngOnInit(): void {
    this.invitationList();
  }

  ngOnDestroy() {
    this.impersonationSubscription.unsubscribe();
    this.disconnectObserver();
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  private disconnectObserver() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.observedElement = undefined;
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.tryResize();
    //
  }

  onGridPreDestroyed() {
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  private tryResize() {
    if (!this.observedElement || !this.gridApi) return;
    const width = this.observedElement.getBoundingClientRect().width;
    if (width > 0) {
      // setTimeout pushes this past the current change detection cycle,
      // giving the grid a tick to finish wiring itself up
      setTimeout(() => this.resizeGrid(), 0);
    }
  }

  private resizeGrid() {
    if (!this.gridApi) return;
    try {
      this.gridApi.sizeColumnsToFit();
    } catch (e) {
      // Grid was destroyed between the check and the call — discard the api
      this.gridApi = undefined;
      this.gridColumnApi = undefined;
    }
  }
}
